import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Lock, UserPlus, Users } from 'lucide-react';
import type { StaffMember } from '@tailoring-crm/shared';
import { UsageMeter } from '../components/billing/usage-meter.js';
import { InviteStaffDialog } from '../components/staff/invite-staff-dialog.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, ListSkeleton } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useRevokeInvite, useStaffInvites, useStaffList } from '../lib/staff/use-staff.js';
import { useCraftRoles } from '../lib/staff/use-craft-roles.js';
import { offerableCraftRoles } from '../lib/staff/craft-roles-api.js';
import { formatDate, formatMobile } from '../lib/format.js';

/**
 * The roster.
 *
 * An owner opens this to answer three questions: who works here, who is on their way in, and
 * how close the shop is to its seat allowance. So the seat line is above the list rather than
 * hidden in billing — hitting a limit at the moment you try to hire someone is the worst place
 * to first learn a limit exists.
 *
 * People who have left stay on the list, greyed and at the bottom. That is the point of
 * deactivation: their work and their payouts are still attributable, and re-hiring the person
 * who covers every wedding season is one tap rather than a new record.
 *
 * A tailor reaching this screen sees only themselves, because that is all the API will return
 * them — the empty-state copy here says so plainly rather than looking like a broken list.
 *
 * The craft-role filter narrows the roster by what people DO on the floor — a description the
 * shop writes for itself, with no bearing on what anybody is allowed to see. It filters the list
 * already fetched rather than asking the server again, and it deliberately leaves the seat figure
 * alone: seats describe the whole shop, and a count that moved with a filter would be a number
 * somebody eventually reads as their allowance.
 */

/** `null` is the "everyone" default; `''` is the explicit "nobody has one set" choice. */
const NO_CRAFT_ROLE = '';

export function StaffPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [inviting, setInviting] = useState(false);
  const [craftRoleFilter, setCraftRoleFilter] = useState<string | null>(null);

  const canManage = role === 'owner' || role === 'manager';
  const staff = useStaffList();
  const invites = useStaffInvites({ enabled: canManage });
  const craftRoles = useCraftRoles();
  const revoke = useRevokeInvite();

  const items = staff.data?.items ?? [];
  const seats = staff.data?.seats;
  const pendingInvites = invites.data ?? [];
  const offerableRoles = offerableCraftRoles(craftRoles.data?.roles);

  const shownItems =
    craftRoleFilter === null
      ? items
      : items.filter((member) =>
          craftRoleFilter === NO_CRAFT_ROLE
            ? member.craftRole === null
            : member.craftRole === craftRoleFilter,
        );

  if (!canManage) {
    // A karigar's own row is genuinely useful — it is where their rates live — but this is not
    // their screen, and pretending otherwise would show them an empty shop.
    const me = items[0];
    return (
      <>
        <PageHeader title={t('staff.title')} />
        {me ? (
          <ul className="flex flex-col gap-2">
            <StaffRow member={me} />
          </ul>
        ) : (
          <EmptyState
            icon={Lock}
            title={t('staff.noAccessTitle')}
            description={t('staff.noAccessHint')}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('staff.title')}
        description={t('staff.description')}
        action={
          <Button onClick={() => setInviting(true)}>
            <UserPlus aria-hidden="true" />
            {t('staff.invite')}
          </Button>
        }
      />

      <div className="flex flex-col gap-6">
        {seats?.usage ? (
          // A meter rather than a sentence: "4 of 10" is read as a fact, a three-quarters-full
          // bar is read as a warning — and the shop hires with its eyes open instead of
          // meeting the ceiling halfway through inviting somebody.
          <div className="rounded-lg border border-border p-3">
            <UsageMeter label={t('billing.usage.seats')} usage={seats.usage} />
          </div>
        ) : seats ? (
          <p className="rounded-lg border border-border p-3 text-sm" role="status">
            {seats.allowance === null
              ? t('staff.seatsUnlimited', { count: seats.activeCount })
              : t('staff.seatsUsed', {
                  count: seats.activeCount,
                  allowance: seats.allowance,
                })}
          </p>
        ) : null}

        {pendingInvites.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {t('staff.pendingInvites')}
            </h3>
            <ul className="flex flex-col gap-2">
              {pendingInvites.map((invite) => (
                <li
                  key={invite.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {invite.name}
                      <Badge variant="secondary">{t(`role.${invite.role}`)}</Badge>
                    </p>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Clock aria-hidden="true" className="size-3.5" />
                      {invite.email} ·{' '}
                      {t('staff.inviteExpires', { date: formatDate(invite.expiresAt) })}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(invite.id)}
                  >
                    {t('staff.revokeInvite')}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {offerableRoles.length > 0 ? (
          <div className="flex items-center gap-2">
            <label htmlFor="staff-craft-role" className="text-sm text-muted-foreground">
              {t('staff.craftRole.filterLabel')}
            </label>
            <select
              id="staff-craft-role"
              value={craftRoleFilter ?? 'all'}
              onChange={(event) =>
                setCraftRoleFilter(event.target.value === 'all' ? null : event.target.value)
              }
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">{t('staff.craftRole.filterAll')}</option>
              {offerableRoles.map((craftRole) => (
                <option key={craftRole.key} value={craftRole.key}>
                  {craftRole.label}
                </option>
              ))}
              {/* Its own option rather than a gap in the list: "who has nobody's trade written
                  down?" is exactly the question this filter gets opened for. */}
              <option value={NO_CRAFT_ROLE}>{t('staff.craftRole.filterNone')}</option>
            </select>
          </div>
        ) : null}

        {staff.isPending ? (
          <ListSkeleton rows={4} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t('staff.emptyTitle')}
            description={t('staff.emptyHint')}
            action={<Button onClick={() => setInviting(true)}>{t('staff.invite')}</Button>}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {shownItems.map((member) => (
              <StaffRow key={member.userId} member={member} linked />
            ))}
          </ul>
        )}
      </div>

      <InviteStaffDialog open={inviting} onOpenChange={setInviting} />
    </>
  );
}

function StaffRow({ member, linked = false }: { member: StaffMember; linked?: boolean }) {
  const { t } = useTranslation();

  const body = (
    <>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          {member.name}
          <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
            {t(`role.${member.role}`)}
          </Badge>
          {member.active ? null : <Badge variant="warning">{t('staff.inactive')}</Badge>}
          {/* Beside the role badge and never in place of it, and styled as plain text rather
              than a badge — the two say completely different things, and a shop that reads a
              trade as a permission is a shop that has been misled by its own screen. */}
          {member.craftRoleLabel ? (
            <span className="text-sm font-normal text-muted-foreground">
              {member.craftRoleLabel}
            </span>
          ) : null}
        </p>
        <p className="text-sm text-muted-foreground">
          {member.phone ? `${member.email} · ${formatMobile(member.phone)}` : member.email}
          {member.specialisations.length > 0
            ? ` · ${member.specialisations.map((stage) => t(`stage.${stage}`)).join(', ')}`
            : ''}
        </p>
      </div>
      {linked ? (
        <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      ) : null}
    </>
  );

  return (
    <li>
      {linked ? (
        <Link
          to={`/staff/${member.userId}`}
          className={`flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            member.active ? '' : 'opacity-70'
          }`}
        >
          {body}
        </Link>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-border p-3">{body}</div>
      )}
    </li>
  );
}

export default StaffPage;
